/**
 * courseGrouping — shared helpers that collapse same-stem course
 * variants into one entry. Used by both PricingPage.vue's catalog/extras
 * grids AND LicensePane.vue's course picker so the UX is consistent.
 *
 * Stem = everything before the first " - " in the course name.
 *   "Alcohol Safety Training - Florida/Employee"  -> "Alcohol Safety Training"
 *   "Workplace Harassment"                        -> "Workplace Harassment"
 *
 * Stems with 2+ courses become a synthetic { isGroup: true, ... } entry
 * with a `variants` array. Stems with one course pass through unchanged
 * with isGroup=false. Order of stems preserved by first-seen.
 */

export function nameStem(name) {
  const s = String(name || '');
  const idx = s.indexOf(' - ');
  return (idx === -1 ? s : s.slice(0, idx)).trim();
}

/**
 * "Florida/Employee" out of "Alcohol Safety Training - Florida/Employee".
 * Falls back to the full name when there's no separator.
 */
export function variantLabel(course) {
  const s = String((course && course.name) || '');
  const idx = s.indexOf(' - ');
  return (idx === -1 ? s : s.slice(idx + 3)).trim();
}

export function groupCourses(list) {
  if (!Array.isArray(list)) return [];
  const map = new Map();
  const order = [];
  list.forEach((c) => {
    if (!c) return;
    const stem = nameStem(c.name);
    if (!map.has(stem)) {
      map.set(stem, []);
      order.push(stem);
    }
    map.get(stem).push(c);
  });
  const out = [];
  order.forEach((stem) => {
    const variants = map.get(stem);
    if (variants.length === 1) {
      const c = variants[0];
      out.push({ isGroup: false, key: 'course-' + c.id, ...c });
    } else {
      const costs = variants.map((v) => Number(v.cost) || 0);
      const minCost = Math.min(...costs);
      const maxCost = Math.max(...costs);
      const lead = variants[0];
      out.push({
        isGroup: true,
        key: 'group-' + stem.replace(/\s+/g, '-').toLowerCase(),
        id: 'group-' + stem,
        name: stem,
        description: lead.description || 'Multiple versions available — pick the one that fits your state / role.',
        category: lead.category,
        length: lead.length,
        certificate_available: lead.certificate_available,
        is_best_seller: variants.some((v) => v.is_best_seller),
        minCost,
        maxCost,
        costSame: minCost === maxCost,
        cost: minCost,
        variants,
        variantCount: variants.length,
      });
    }
  });
  return out;
}
